/**
 * ISBD framing around a `$d` date statement (CZNA-876).
 *
 * <p>A corporate `110$d` / `410$d` does not hold a bare date. MARC 21 keeps ISBD
 * punctuation inside the subfield it belongs to, so the qualifier group
 * `(8. : 2003 : Alicante, Španělsko)` is catalogued as
 * `$n (8. : $d 2003 : $c Alicante, Španělsko)` — the opening parenthesis rides on
 * whichever subfield comes first, the separating colon rides on the element it
 * follows, and the closing parenthesis rides on the last one. A `$d` therefore
 * routinely arrives as `2003 :`, `(2006 :`, `1464)` or `(1962-1965).`, and the
 * date is what sits INSIDE that framing.
 *
 * <p>Both the validator and the codec were inherited from personal names, where
 * `$d` never carries framing, and both read the whole string as the date.
 * Measured over the live corpus on 30. 8. 2026 (5 987 values in `110$d`+`410$d`):
 * 4 961 (82,9 %) drew the yellow "unusual format" complaint and 4 913 (82,1 %)
 * were silently rewritten on edit — `2003 :` → `2003 :-`, i.e. "still ongoing",
 * which is not what the record says. The two clusters `9999 :` and `(9999 :`
 * alone are 4 724 of the 5 987.
 *
 * <p><b>The framing is real cataloguing, not import noise.</b> In the same corpus
 * the sibling subfields carry the matching halves — `$n` holds `(8. :` and `$c`
 * holds `Alicante, Španělsko)` — so the punctuation is distributed across the
 * group exactly as MARC prescribes. That is why this module PEELS the framing to
 * judge and to decide, and never strips it from a value that gets stored: the
 * characters belong to the field.
 */
/**
 * The characters that can frame a date statement.
 *
 * <p>Measured rather than guessed: across the 5 987 corporate values the leading
 * run is either empty or `(`, and the trailing run is one of
 * `` (empty), ` :`, `-`, `)`, `).`, `.)`, `.).`, `. :`, ` -`. The hyphen is
 * deliberately absent from both classes, because a trailing hyphen is the "still
 * ongoing" marker (`2000-`, 438 rows) and carries meaning.
 *
 * <p><b>These match a RUN of those characters, not that enumerated list.</b>
 * `2003 :::`, `2003.:.:.` and `(((1464` all peel to the bare year and validate
 * clean. That is deliberate: `(2006 :` is legitimately unbalanced, so nothing at
 * the whole-value level can pair the parentheses, and the worst case of being too
 * tolerant here is a warning that does not appear on a value nobody catalogues.
 * Do not read the measured list above as a constraint the regexes enforce.
 */
var LEADING_FRAME = /^[\s(]+/;
var TRAILING_FRAME = /[\s.:)]+$/;
/**
 * True when `value` ends in punctuation that CLOSES the date statement. The
 * optional final period is AACR2's end-of-statement mark sitting outside the
 * closing parenthesis, as in `(1464).` and `(2007 prosinec 13.).` — 40 corporate
 * rows, and without it they would be the only closed shapes still reopened.
 */
var CLOSING = /[:)]\s*\.?\s*$/;
/**
 * Strips the ISBD framing so the caller sees the date alone.
 *
 * <p>Returns the input unchanged when peeling would leave nothing with a digit in
 * it, so `(Fleur R.)` does not become `Fleur R` and a lone `.` does not become
 * `''`. <b>This is not what keeps the "no date signal at all" error alive</b> —
 * `validateYearQualifier` takes that decision on the un-peeled value, so a
 * digit-free peel could not reach the accept-empty branch anyway. The guard is
 * here because this is an exported primitive and a peel that can answer `''` for
 * a non-empty input is a footgun for the next consumer, which will not have that
 * ordering to rely on.
 */
export var peelIsbdFraming = function (value) {
    var raw = (value || '').trim();
    if (!raw)
        return raw;
    var peeled = raw.replace(LEADING_FRAME, '').replace(TRAILING_FRAME, '').trim();
    return /\d/.test(peeled) ? peeled : raw;
};
/**
 * True when the value's date statement is CLOSED by ISBD punctuation — a `)` that
 * shuts the qualifier group, or a `:` that hands over to the next subfield.
 *
 * <p>This is the encoder's licence not to append the "still ongoing" hyphen. A
 * closed statement is finished by construction: `$d 2003 : $c Alicante` says the
 * meeting was in 2003, not that it has been running ever since. It is the same
 * shape of rule as the floruit exception in `summarizeSubfieldD` and it is stated
 * over punctuation the cataloguer typed, not over a guess about the field's type.
 *
 * <p>A trailing full stop alone does NOT count. AACR2 closes a date statement with
 * a period whether or not the statement is finished, so the period says nothing
 * about openness — `1960-.` and `1750.` would be indistinguishable. It is also not
 * needed: all 956 personal `$d` values that end in a period and hold no hyphen are
 * floruits (`činný 13. století.`), which the floruit rule already declines to
 * reopen. Admitting the period here would buy nothing and would put a guess where
 * a measurement can stand.
 */
export var isClosedDateStatement = function (value) { return CLOSING.test((value || '').trim()); };
